import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Loader2, RefreshCw } from 'lucide-react';
import { Platform } from '../../../types/Platforms';
import { formatPlatformName } from '../../../services/helperService';
import { assignSetupSupplierFiles, downloadSetupArchive, FileReceipt, recognizeSetupSupplierFiles, SetupRecognitionResult, SetupStep, SetupSupplierFileTarget } from '../../../services/accountSetup/accountSetupService';
import { secondaryText } from '../../../styling/stylingHelper';
import { setupButton, setupErrorKey, setupInput, setupSecondary } from './SetupPrimitives';

/** Selection belongs to the current file view; recognition uses bounded, revision-chained requests. */
export function useSetupInvoiceIntake(step: SetupStep, filter: string | null, onChange: () => Promise<void>, onError: (message: string) => void) {
  const [selected, setSelected] = useState<string[]>([]);
  const [target, setTarget] = useState<SetupSupplierFileTarget | ''>('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [result, setResult] = useState<SetupRecognitionResult | null>(null);
  const active = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => { setSelected([]); setTarget(''); }, [filter, step.restaurantId]);
  useEffect(() => { setResult(null); }, [step.restaurantId]);
  const availableIds = step.files.filter(file => filter === 'all' || supplierFileGroup(file) === filter).map(file => file.id).join(',');
  useEffect(() => { setSelected([]); }, [availableIds]);
  const disabled = busy || step.cloudStatus === 'not_applicable';
  const run = async (action: () => Promise<void>) => {
    if (active.current || step.cloudStatus === 'not_applicable') return;
    active.current = true; setBusy(true); onError(''); setResult(null);
    try { await action(); }
    catch (failure) { console.error('Setup supplier intake action failed', failure); if (mounted.current) onError(setupErrorKey(failure)); }
    finally {
      try { await onChange(); }
      catch (failure) { console.error('Failed to reload setup after supplier intake action', failure); if (mounted.current) onError(setupErrorKey(failure)); }
      active.current = false;
      if (mounted.current) { setBusy(false); setProgress(null); }
    }
  };
  const unknown = step.files.filter(file => supplierFileGroup(file) === 'other');
  const refresh = () => run(async () => {
    let revision = step.revision;
    const totals: SetupRecognitionResult = { revision, matched: 0, unresolved: 0, failed: 0 };
    setProgress({ done: 0, total: unknown.length });
    for (let offset = 0; offset < unknown.length && mounted.current; offset += 10) {
      const ids = unknown.slice(offset, offset + 10).map(file => file.id);
      const next = await recognizeSetupSupplierFiles(step.restaurantId, revision, ids);
      revision = next.revision;
      totals.revision = revision;
      totals.matched += next.matched; totals.unresolved += next.unresolved; totals.failed += next.failed;
      if (mounted.current) setProgress({ done: Math.min(offset + 10, unknown.length), total: unknown.length });
    }
    if (mounted.current) setResult(totals);
  });
  const assign = () => run(async () => {
    if (!selected.length || !target) return;
    await assignSetupSupplierFiles(step.restaurantId, step.revision, selected, target);
    if (mounted.current) { setSelected([]); setTarget(''); }
  });
  const download = () => run(() => downloadSetupArchive(step.restaurantId, 'supplier_invoices', 'unknown'));
  const toggle = (id: string) => setSelected(current => current.includes(id) ? current.filter(value => value !== id) : [...current, id]);
  return { selected, setSelected, target, setTarget, disabled, busy, progress, result, unknown, refresh, assign, download, toggle };
}

export function supplierFileGroup(file: FileReceipt): string {
  if (file.notInvoice) return 'not_invoice';
  return file.supplierPlatform && file.supplierPlatform !== Platform.ANY_INVOICE && Object.values(Platform).includes(file.supplierPlatform as Platform) ? file.supplierPlatform : 'other';
}

type Intake = ReturnType<typeof useSetupInvoiceIntake>;
export function AdminSetupInvoiceActions({ intake }: { intake: Intake }) {
  const { t } = useTranslation();
  return <div className="space-y-2">
    <div className="flex flex-wrap gap-2">
      <button className={setupSecondary} disabled={intake.disabled || !intake.unknown.length} onClick={() => void intake.refresh()}><RefreshCw className="h-4 w-4" />{t('accountSetup.intake.refresh')}</button>
      <button className={setupSecondary} disabled={intake.disabled || !intake.unknown.length} onClick={() => void intake.download()}><Download className="h-4 w-4" />{t('accountSetup.intake.downloadUnknown')}</button>
    </div>
    {intake.busy && <p role="status" className={`flex items-center gap-2 text-sm ${secondaryText}`}><Loader2 className="h-4 w-4 animate-spin" />{intake.progress ? t('accountSetup.intake.progress', intake.progress) : t('accountSetup.intake.working')}</p>}
    {intake.result && <p role="status" className={`text-sm ${secondaryText}`}>{t('accountSetup.intake.result', { ...intake.result })}</p>}
  </div>;
}

export function AdminSetupInvoiceAssignment({ intake, files }: { intake: Intake; files: FileReceipt[] }) {
  const { t, i18n } = useTranslation();
  const platforms = Object.values(Platform).filter((value): value is Exclude<Platform, Platform.ANY_INVOICE> => typeof value === 'string' && value !== Platform.ANY_INVOICE)
    .sort((a, b) => formatPlatformName(a).localeCompare(formatPlatformName(b), i18n.language));
  const all = files.length > 0 && files.every(file => intake.selected.includes(file.id));
  return <fieldset disabled={intake.disabled} className="mb-4 space-y-3">
    <label className="flex min-h-[44px] cursor-pointer items-center gap-3 text-sm"><input type="checkbox" checked={all} onChange={() => intake.setSelected(all ? [] : files.map(file => file.id))} className="h-5 w-5" />{t('accountSetup.intake.selectAll', { count: files.length })}</label>
    <p className={`text-sm ${secondaryText}`}>{t('accountSetup.intake.selected', { count: intake.selected.length })}</p>
    <label className="block space-y-1 text-sm"><span>{t('accountSetup.intake.assignTo')}</span><select className={setupInput} value={intake.target} onChange={event => intake.setTarget(event.target.value as SetupSupplierFileTarget | '')}>
      <option value="">{t('accountSetup.intake.chooseSupplier')}</option>
      {platforms.map(platform => <option key={platform} value={platform}>{formatPlatformName(platform)}</option>)}
      <option value="other">{t('accountSetup.upload.otherSuppliers')}</option>
      <option value="not_invoice">{t('accountSetup.intake.notInvoice')}</option>
    </select></label>
    <button className={setupButton} disabled={intake.disabled || !intake.selected.length || !intake.target} onClick={() => void intake.assign()}>{intake.busy && <Loader2 className="h-4 w-4 animate-spin" />}{t('accountSetup.intake.apply')}</button>
  </fieldset>;
}
